/**
 * F4 Timeseries chart — stacked area de status counts a lo largo del tiempo.
 * Lee history snapshots y muestra cuántas secciones estaban en cada color.
 *
 * 5 secciones tienen columna *_status explícita en admin_health_snapshots.
 * 3 secciones (gitnexus/ml_tokens/mcp_servers) viven solo en reasons jsonb;
 * inferimos su status: null reason → green, texto → yellow (no podemos
 * distinguir red de yellow sin status explícito en el snapshot).
 */

import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

type Light = 'green' | 'yellow' | 'red';

interface HealthSnapshot {
  id: number;
  captured_at: string;
  overall: Light;
  pm2_status?: Light;
  processes_running_status?: Light;
  queues_status?: Light;
  disk_status?: Light;
  errors_recent_status?: Light;
  reasons?: Record<string, string | null>;
}

interface ChartPoint {
  time: string;
  captured_at: string;
  red: number;
  yellow: number;
  green: number;
  overall: Light;
  reasons: Record<string, string | null>;
}

interface Props {
  snapshots: HealthSnapshot[];
}

const REASON_KEYS_INFERRED: (keyof NonNullable<HealthSnapshot['reasons']>)[] = [
  'gitnexus',
  'ml_tokens',
  'mcp_servers',
];

function snapshotToPoint(snap: HealthSnapshot): ChartPoint {
  const explicitStatuses: (Light | undefined)[] = [
    snap.pm2_status,
    snap.processes_running_status,
    snap.queues_status,
    snap.disk_status,
    snap.errors_recent_status,
  ];

  const reasons = snap.reasons || {};
  const inferredStatuses: Light[] = REASON_KEYS_INFERRED.map((k) =>
    reasons[k] ? 'yellow' : 'green'
  );

  const all: Light[] = [
    ...explicitStatuses.filter((s): s is Light => s !== undefined),
    ...inferredStatuses,
  ];

  return {
    time: new Date(snap.captured_at).toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
    captured_at: snap.captured_at,
    red: all.filter((s) => s === 'red').length,
    yellow: all.filter((s) => s === 'yellow').length,
    green: all.filter((s) => s === 'green').length,
    overall: snap.overall,
    reasons,
  };
}

function CustomTooltip(props: Partial<TooltipContentProps<ValueType, NameType>>) {
  const { active, payload } = props;
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload as ChartPoint;
  const activeReasons = Object.entries(point.reasons || {}).filter(([, r]) => r);

  return (
    <div className="bg-white border border-gray-300 rounded shadow-lg p-3 text-xs max-w-sm">
      <div className="font-mono text-gray-600 mb-1">
        {new Date(point.captured_at).toLocaleString('es-AR')}
      </div>
      <div className="font-semibold mb-2">
        Overall: <span className={`uppercase ${
          point.overall === 'red' ? 'text-red-600' :
          point.overall === 'yellow' ? 'text-yellow-600' : 'text-green-600'
        }`}>{point.overall}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="text-green-700">🟢 {point.green}</div>
        <div className="text-yellow-700">🟡 {point.yellow}</div>
        <div className="text-red-700">🔴 {point.red}</div>
      </div>
      {activeReasons.length > 0 && (
        <div className="border-t pt-2 space-y-0.5">
          {activeReasons.map(([k, r]) => (
            <div key={k} className="text-gray-700">
              <span className="font-mono text-gray-500">{k}:</span> {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HealthTimeseriesChart({ snapshots }: Props) {
  // Reverse so most recent goes right (history endpoint returns newest-first)
  const data = useMemo(
    () => [...snapshots].reverse().map(snapshotToPoint),
    [snapshots]
  );

  if (data.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        Sin snapshots históricos todavía.
      </div>
    );
  }

  return (
    <div className="bg-white border rounded p-4">
      <div className="text-sm text-gray-600 mb-2">
        Distribución de estados por sección a lo largo del tiempo (
        {data.length} snapshots, total 8 secciones)
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis tick={{ fontSize: 11 }} domain={[0, 8]} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area
            type="step"
            dataKey="green"
            stackId="1"
            stroke="#16a34a"
            fill="#22c55e"
            fillOpacity={0.6}
            name="Verde"
            isAnimationActive={false}
          />
          <Area
            type="step"
            dataKey="yellow"
            stackId="1"
            stroke="#ca8a04"
            fill="#eab308"
            fillOpacity={0.7}
            name="Amarillo"
            isAnimationActive={false}
          />
          <Area
            type="step"
            dataKey="red"
            stackId="1"
            stroke="#dc2626"
            fill="#ef4444"
            fillOpacity={0.8}
            name="Rojo"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
